import React, { useState, useEffect } from "react";
import { FormLabel, FormToggle, FormElement, FormInput, Button } from "../../../components/StyledElements";
import { useLiveEvents } from "../../../contexts/LiveEventsContext";
import { getAbbreviatedTimeZone, fromDateToString, getLocalDateFromString } from "../../../utils/utils";
import swal from "sweetalert2";

const EventTypeSelector = ({ value = "live", onChange = () => { } }) => {
  return (
    <select onChange={onChange} style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", fontSize: "16px" }} value={value}>
      <option value="live">Live</option>
      <option value="recorded">Pre-recorded</option>
      <option value="scheduled">Scheduled</option>
    </select>
  );
};

export const CoreDetails = ({ style = {}, onSelectTab = () => { } }) => {
  const { updatedEvent, setUpdatedEvent } = useLiveEvents();
  const [localLiveEvent, setLocalLiveEvent] = useState(updatedEvent);
  const [localOpenDate, setLocalOpenDate] = useState("");
  const [localStartDate, setLocalStartDate] = useState("");
  const [localRecordingDate, setLocalRecordingDate] = useState("");

  useEffect(() => {

    setLocalOpenDate(
      localLiveEvent?.open_date
        ? fromDateToString(getLocalDateFromString(localLiveEvent?.open_date), false)
        : ""
    );

    setLocalStartDate(
      localLiveEvent?.start_date
        ? fromDateToString(getLocalDateFromString(localLiveEvent?.start_date), false)
        : ""
    );

    setLocalRecordingDate(
      localLiveEvent?.recording_date
        ? fromDateToString(getLocalDateFromString(localLiveEvent?.recording_date), false)
        : ""
    );

    console.log("!!! LOCAL LIVE EVENT: ", localLiveEvent);
  }, [localLiveEvent]);

  useEffect(() => {
    console.log("!!! LOCAL OPEN DATE CHANGED: ", localOpenDate);
    const newUpdatedEvent = { ...updatedEvent };
    newUpdatedEvent.local_open_date = localOpenDate;
    newUpdatedEvent.local_start_date = localStartDate;
    newUpdatedEvent.local_recording_date = localRecordingDate;
    console.log("!!! NEW UPDATED EVENT WITH LOCAL DATES: ", newUpdatedEvent);
    setUpdatedEvent(newUpdatedEvent);

  }, [localOpenDate, localStartDate, localRecordingDate]);

  const handleDateChange = (name, value) => {
    console.log("!!! HANDLE CHANGE: ", name, value);
    const event = { ...updatedEvent };
    event[name] = value;
    console.log("!!! AFTER CHANGE: ", event);
    setUpdatedEvent(event);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", ...style }}>
      <FormElement style={{ flexDirection: "row", display: "flex", alignItems: "center", gap: "10px" }}>
        <FormLabel label={`Published (${updatedEvent?.published ? "Yes" : "No"})`} style={{ margin: 0 }} />
        <FormToggle
          checked={updatedEvent?.published ?? false}
          onChange={(e) => {
            updatedEvent.published = e.target.checked;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <FormElement>
        <FormLabel label="Public ID" />
        <FormInput disabled type="text" value={updatedEvent?.public_id ?? ""} />
      </FormElement>
      <FormElement>
        <FormLabel label="Event Title" />
        <FormInput type="text" placeholder="Enter event title" value={updatedEvent?.title ?? ""}
          onChange={(e) => {
            updatedEvent.title = e.target.value;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
        <FormElement style={{ width: "50%" }}>
          <FormLabel label="Event Type" />
          <EventTypeSelector
            value={updatedEvent?.event_type ?? "live"}
            onChange={(e) => {
              updatedEvent.event_type = e.target.value;
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
        <FormElement style={{ width: "50%" }}>
          <FormLabel label="Price" />
          <FormInput type="number" placeholder="Enter event price" step="0.50" min="0.00"
            value={updatedEvent?.price ?? "0.00"}
            onChange={(e) => {
              updatedEvent.price = parseFloat(e.target.value);
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
      </div>
      {
        updatedEvent?.event_type === "recorded" ? (
          <FormElement>
            <FormLabel label={`Recording Time (${getAbbreviatedTimeZone()}) - When the event is scheduled to be recorded`} />
            <FormInput type="datetime-local"
              value={localRecordingDate}
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                const utcString = newDate.toISOString().substr(0, 16);
                setLocalRecordingDate(e.target.value);
                handleDateChange("recording_date", utcString);
              }}
            />
          </FormElement>) : null}
      <FormElement>
        <FormLabel label={`Open Time (${getAbbreviatedTimeZone()}) - When the event is open for questions`} />
        <FormInput type="datetime-local"
          value={localOpenDate}
          min={localRecordingDate || undefined}
          onBlur={(e) => {
            // Close the date picker
            e.target.blur();
          }}
          onChange={(e) => {
            const minutesToAdd = 30;
            const recordingDatePlus30 = new Date(new Date(localRecordingDate).getTime() + minutesToAdd * 60000);
            const newDate = new Date(e.target.value);
            if (newDate < recordingDatePlus30) {
              // adjust open date to be at least 30 minutes after recording date
              swal.fire({
                title: "Open Time Too Early",
                text: `Open Time must be at least ${minutesToAdd} minutes after the recording Time. Do you want to adjust the open time to meet this requirement?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, adjust it",
                cancelButtonText: "No, I'll choose a different time",
              }).then((result) => {
                if (result.value) {
                  newDate.setTime(recordingDatePlus30.getTime());
                  setLocalOpenDate(fromDateToString(newDate, false));
                  handleDateChange("open_date", newDate.toISOString().substr(0, 16));
                }
              });
              return;
            }
            setLocalOpenDate(e.target.value);
            handleDateChange("open_date", newDate.toISOString().substr(0, 16));
          }}
        />

      </FormElement>
      <FormElement>
        <FormLabel label={`Start Time (${getAbbreviatedTimeZone()}) - When the event starts`} />
        <FormInput type="datetime-local"
          value={localStartDate}
          min={localOpenDate || undefined}

          onBlur={(e) => {
            // Close the date picker
            e.target.blur();
          }}

          onChange={(e) => {
            const newDate = new Date(e.target.value);
            const utcString = newDate.toISOString().substr(0, 16);

            if (newDate < new Date(localOpenDate)) {
              // adjust start date to be at least the same as open date
              swal.fire({
                title: "Start Time Too Early",
                text: `Start Time must be the same or after the open time. Do you want to adjust the start time to meet this requirement?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, adjust it",
                cancelButtonText: "No, I'll choose a different time",
              }).then((result) => {
                if (result.value) {
                  const openDate = new Date(localOpenDate);
                  setLocalStartDate(fromDateToString(openDate, false));
                  handleDateChange("start_date", openDate.toISOString().substr(0, 16));
                }
              }
              );
            }


            setLocalStartDate(e.target.value);
            handleDateChange("start_date", utcString);
          }}
        />
      </FormElement>
      <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
        <FormElement style={{ flex: 1 }}>
          <FormLabel label="Topic" />
          <FormInput type="text" value={updatedEvent?.topic ?? ""}
            onChange={(e) => {
              updatedEvent.topic = e.target.value;
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
        <FormElement style={{ flex: 1 }}>
          <FormLabel label="Event Category" />
          <FormInput type="text" placeholder="Enter event category" value={updatedEvent?.category ?? ""}
            onChange={(e) => {
              updatedEvent.category = e.target.value;
              setUpdatedEvent({ ...updatedEvent });
            }}
          />
        </FormElement>
      </div>
      <FormElement>
        <FormLabel label="Description" />
        <FormInput
          as="textarea"
          rows={5}
          placeholder="Enter event description"
          style={{ resize: "vertical" }}
          value={updatedEvent?.description ?? ""}
          onChange={(e) => {
            updatedEvent.description = e.target.value;
            setUpdatedEvent({ ...updatedEvent });
          }}
        />
      </FormElement>
      <div
        style={{
          marginTop: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "10px"
        }}
      >
        <Button
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "auto",
            padding: "0px 16px"
          }}

          onClick={() => {
            onSelectTab("media-docs");
          }}
        >+ Media</Button>
      </div>
    </div>
  );
};

export default CoreDetails;
